/**
 * The Guts Check — twelve questions, four dimensions, four levels.
 *
 * Kagan's tool, handed over as standalone HTML on 2026-08-25. The copy is his
 * and Halil's; it is transcribed here rather than rewritten, and the two places
 * it was CHANGED are both recorded:
 *
 * 1. **The facilitator notes are not here.** The source had four coaching notes
 *    per level behind a "Show facilitator notes" button on the same page the
 *    prospect uses — "Do not sell technology into this room", "Listen for who
 *    flinches at the ownership question". They now live in
 *    `website.tool_playbook` and reach the follow-up task, because anything in
 *    this file is in the client bundle and therefore readable by exactly the
 *    person it was written about.
 * 2. **It gates on an email and it did not before.** All three tools capture a
 *    lead now (Kagan, same day). The gate sits between the questions and the
 *    report, not in front of the questions — see `tools/LeadGate.tsx`.
 *
 * The scoring is unchanged: each option is worth its index, 0–3, so the total
 * is 0–36 and a dimension average is 0–3. The level thresholds are the source's
 * (`< 0.75`, `< 1.55`, `< 2.30`, else 3) and are deliberately not round — they
 * were tuned so that a single generous answer does not lift an organisation a
 * whole level.
 */

/** A dimension: the group a question belongs to, and the bar on the report. */
export interface GutsDim {
  name: string
  sub: string
}

export interface GutsQuestion {
  /** Index into `dims`. */
  dim: number
  text: string
  /** The line under the question. It is doing real work — it says what "counts". */
  hint: string
  /** Exactly four, worth 0–3 in order. Atomic: a locale gives all four or none. */
  options: string[]
}

export interface GutsLevel {
  /** The band's machine key, sent to the server to find the coaching notes. */
  band: string
  name: string
  headline: string
  body: string[]
  /** The question to leave them with. The sharpest thing on the page. */
  provocation: string
  moves: { title: string; text: string }[]
}

export const gutsCheck = {
  dims: [
    { name: 'Ambition', sub: 'Where leadership is aiming' },
    { name: 'Decision & ownership', sub: 'Who signed, and what they risked' },
    { name: 'Execution', sub: 'What actually runs, every day' },
    { name: 'Proof', sub: 'Impact you could defend to a CFO' },
  ] as GutsDim[],

  questions: [
    {
      dim: 0,
      text: 'What does your board expect from AI over the next 24 months?',
      hint: 'Expectation, not aspiration. What would they actually hold someone to?',
      options: [
        'It has not been a substantive board topic.',
        "Measurable cost and productivity savings inside today's processes.",
        'AI embedded in how core processes run, with named owners and targets.',
        'A materially different operating model, with parts of the business redesigned from a blank sheet.',
      ],
    },
    {
      dim: 0,
      text: 'If agentic AI delivered everything you hoped for, what would be different?',
      hint: 'Be honest about the size of the prize you are actually chasing.',
      options: [
        'Little structurally. We would be relieved to have kept pace with peers.',
        'The same business, run at a meaningfully lower cost to serve.',
        'The same business, run through a different process and organisation design.',
        'We would compete on things we cannot do today: new offers, new economics.',
      ],
    },
    {
      dim: 0,
      text: 'How would your top team describe the ambition out loud?',
      hint: 'The words matter. They set the ceiling on what anyone dares to propose.',
      options: [
        'It has not been articulated.',
        'Efficiency: do the same things, cheaper and faster.',
        'Maturity: lift the capability of core processes end to end.',
        'Reinvention: rebuild the unit or process green-field around agents.',
      ],
    },
    {
      dim: 1,
      text: 'Who owns the agentic AI agenda?',
      hint: 'Ownership means a name, a mandate, and consequences.',
      options: [
        'No one. It surfaces occasionally as an IT topic.',
        'IT or innovation, as one initiative among many.',
        'A named executive with a mandate and a budget line.',
        'The CEO and leadership team, with P&L owners individually accountable for outcomes.',
      ],
    },
    {
      dim: 1,
      text: 'What has leadership decided to stop, retire or reorganise because of AI?',
      hint: 'Adding is easy. Subtracting is the decision that costs something.',
      options: [
        'Nothing, and the question has not been asked.',
        'Nothing yet; so far AI has been added on top of existing processes.',
        'Some processes have been redesigned and some roles have changed.',
        'We have retired systems, redesigned units and moved people, deliberately and visibly.',
      ],
    },
    {
      dim: 1,
      text: 'How is the work funded?',
      hint: 'Budget is the most reliable statement of intent an organisation makes.',
      options: [
        'There is no dedicated budget.',
        'Ad hoc, project by project, largely from underspend elsewhere.',
        'A multi-year programme budget, defended in the planning cycle.',
        'Committed capital with board milestones, and an accepted short-term margin impact.',
      ],
    },
    {
      dim: 2,
      text: 'What is genuinely in production, with real users doing real work every day?',
      hint: "Production means someone's job breaks if it stops.",
      options: [
        'Nothing. Individuals use public chatbots on their own initiative.',
        'A licensed copilot rolled out broadly; usage is optional and uneven.',
        'Several agents running inside real workflows, with defined hand-offs to people.',
        'Agents are a standard component of the operating model, on a shared platform, reused across units.',
      ],
    },
    {
      dim: 2,
      text: 'Can agents reach your data and your core systems?',
      hint: 'Agentic AI fails on plumbing far more often than on models.',
      options: [
        'We have not assessed it.',
        'We know the landscape is fragmented; the work has not started.',
        'Core domains are being cleaned, governed and exposed through APIs.',
        'Agents reliably read from and write to core systems under governance.',
      ],
    },
    {
      dim: 2,
      text: 'How do risk, legal and audit show up?',
      hint: 'Governance is either a brake or a set of rails. Which is it here?',
      options: [
        'We have blocked AI tools rather than governed them.',
        'A policy document exists; enforcement and interpretation are unclear.',
        'Clear guardrails, human-in-the-loop rules, and an approval path measured in weeks, not quarters.',
        'Risk and audit are designed into delivery from day one, and governance accelerates us.',
      ],
    },
    {
      dim: 3,
      text: 'How do you measure the impact of AI?',
      hint: "Anything that is not in someone's budget is not yet a result.",
      options: [
        'We do not measure it.',
        'Activity: licences issued, logins, number of pilots launched.',
        'Process: cycle time, cost per transaction, quality, error rates on the redesigned flows.',
        'P&L: the savings and the growth sit in the budget of the accountable owner.',
      ],
    },
    {
      dim: 3,
      text: 'What happens to pilots?',
      hint: 'A portfolio of permanent pilots is a way of avoiding a decision.',
      options: [
        'There are no pilots to speak of.',
        'Many pilots, few graduate, and we rarely stop anything formally.',
        'A clear gate: pilots either scale within a set period or are shut down.',
        'We scale by default; not investing is the option that requires justification.',
      ],
    },
    {
      dim: 3,
      text: 'How does the organisation itself react?',
      hint: 'The middle of the organisation decides whether any of this survives contact with reality.',
      options: [
        'Caution and avoidance are the dominant reactions.',
        'Curiosity in pockets; the management layer is unconvinced.',
        'Managers are being trained to redesign their own processes with agents.',
        'Teams propose and ship agentic changes without waiting for a central programme.',
      ],
    },
  ] as GutsQuestion[],

  /** The ladder shown on the report, with the reader's rung marked. */
  ladder: [
    { name: 'Ignorance', desc: 'I don’t want to change anything.' },
    { name: 'Efficiency', desc: 'Cut waste, delays, labour.' },
    { name: 'Maturity', desc: 'Redesign the flow around agents.' },
    { name: 'Reinvention', desc: 'Rebuild it from green-field.' },
  ],

  levels: [
    {
      band: 'ignorance',
      name: 'Ignorance',
      headline: 'You are not behind on technology. You are behind on the decision.',
      body: [
        "Nothing in your answers suggests your organisation is incapable. It suggests that agentic AI has not yet been made anybody's problem, and a decision that is never taken in the room gets taken by default, quarter after quarter.",
        'This is a defensible position for about as long as your competitors share it. The moment one of them moves from cost-cutting to cost structure, the gap stops being a technology gap and becomes an economics gap. Those are much harder to close, because by then they can outspend you and undercut you at the same time.',
        'The first move is not a platform, a vendor or a pilot. It is a named executive, a mandate and a date.',
      ],
      provocation:
        'If a direct competitor announced next quarter that they had taken 30% out of their cost to serve using agents, what would you do in the first 30 days, and what is stopping you from doing that now?',
      moves: [
        {
          title: 'Name the owner.',
          text: 'One executive, on the record, with a mandate that survives the next reorganisation.',
        },
        {
          title: 'Pick two processes, not twenty.',
          text: 'High volume, rules-heavy, measurable. Baseline them properly before anyone touches a model.',
        },
        {
          title: 'Fund a decision, not an experiment.',
          text: 'A small budget with a hard date at which you either scale or stop, and say so publicly.',
        },
      ],
    },
    {
      band: 'efficiency',
      name: 'Efficiency',
      headline: 'You are using AI to be less wasteful. Your competitors are using it to be different.',
      body: [
        'Credit where it is due: you have started, and the savings are probably real. Efficiency is the easiest ambition to defend in a budget meeting, and it is also the cheapest for anyone else to copy.',
        'The risk at this level is subtle. Automating a process is an implicit endorsement of that process. Do it long enough and you have made your existing way of working faster, cheaper and considerably harder to change: you have paved the cowpath.',
        'No organisation has ever reinvented itself from a savings target. At some point someone has to ask what this business would look like if it were designed today, and be given the authority to answer.',
      ],
      provocation:
        'Name one thing you have stopped doing entirely because of AI. If you cannot, you have automated your waste rather than removed it.',
      moves: [
        {
          title: 'Move from tasks to flows.',
          text: 'Take one end-to-end journey, such as order to cash, quote to bind or hire to productive, and redesign it whole rather than step by step.',
        },
        {
          title: 'Change what you count.',
          text: 'Retire licence and login metrics. Report cycle time, cost per transaction, and quality on the flows you have touched.',
        },
        {
          title: 'Put the redesign in the line, not in innovation.',
          text: 'The process owner must own the outcome, or it will not survive the first busy quarter.',
        },
      ],
    },
    {
      band: 'maturity',
      name: 'Maturity',
      headline: 'You have built something real. The question now is whether you will protect it or push it.',
      body: [
        'This is further than most. You have agents in real workflows, an owner, a budget, and metrics that a CFO would recognise. That took decisions, and decisions cost political capital.',
        'The characteristic failure at this level is not collapse but comfort. The organisation becomes very good at running agents inside processes it has never seriously questioned, governance hardens into a queue, and the programme starts optimising for its own continuity rather than for the business it was meant to change.',
        'Level 3 is not more of this. It is a different question: not ‘how do we improve this unit?’ but ‘would we build this unit at all, and if so, how?’ That question is uncomfortable precisely because the answer usually implicates something you have already invested in.',
      ],
      provocation:
        'Which of your current processes would you not rebuild if you were starting this unit today, and what exactly is keeping it alive?',
      moves: [
        {
          title: 'Run one green-field.',
          text: 'Take a single unit or product line and design it from zero, agent-first, with a real P&L and a real deadline.',
        },
        {
          title: 'Make scaling the default.',
          text: 'Invert the burden of proof: not investing should be the option that requires a written justification.',
        },
        {
          title: 'Move capability into the line.',
          text: 'If only the central team can build agents, you have a bottleneck with a budget, not a capability.',
        },
      ],
    },
    {
      band: 'reinvention',
      name: 'Reinvention',
      headline: 'The hard part is no longer the technology. It is holding your nerve.',
      body: [
        'You are in rare company: ambition, ownership, execution and proof are aligned, and the organisation has already absorbed structural change rather than merely discussing it.',
        'The risk profile now inverts. The threat is not inertia but relapse: a difficult quarter, a leadership change, an incident, and the old operating model quietly reasserts itself through exception handling, shadow processes and re-hired workarounds. Reinvention is rarely reversed by decision; it is reversed by drift.',
        'The second risk is narrowness. Success in one unit tends to become a showcase rather than a template. The organisations that compound are those that treat the green-field build as the new default and force every other unit to justify why it is different.',
      ],
      provocation:
        'Where in the business is the old operating model quietly reasserting itself, and who is being rewarded for letting it?',
      moves: [
        {
          title: 'Institutionalise the reset.',
          text: 'Put the green-field question into the annual planning cycle so it is asked by process, not by personality.',
        },
        {
          title: 'Watch for drift, not failure.',
          text: 'Track exceptions, manual overrides and workarounds. They are the early warning that the old model is returning.',
        },
        {
          title: 'Export the model.',
          text: 'Turn the successful unit into a reusable pattern with a mandate, not a case study with a slide.',
        },
      ],
    },
  ] as GutsLevel[],

  gap: {
    aheadTitle: 'Ambition – reality gap',
    aheadBody: [
      'Your stated ambition sits at Level {ambition}. Your {laggard} sits at Level {weakest}. That distance is not a planning problem; it is the most reliable predictor of a transformation that gets announced and then quietly rescoped.',
      'Either fund the gap or lower the language. Carrying both is what erodes credibility with the board and with the people you are asking to change.',
    ],
    behindTitle: 'Under-claimed',
    behindBody: [
      'You are further along in practice than your stated ambition suggests. That is a better problem than the reverse, but it caps what the organisation will attempt, because nobody proposes more than the language allows.',
    ],
    laggards: ['decision and ownership', 'execution', 'proof of impact'],
  },
}
